import { BrowserRouter } from 'react-router-dom';
import { SnackbarProvider } from 'notistack';
import OIDCAuthCheck from './oidcAuthCheck';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import theme from '../theme';
import awsExports from '../aws-exports';
import '@aws-amplify/ui-react/styles.css';
import { Close as CloseIcon } from '@mui/icons-material';
import TitleManager from '../systems/TitleManager/TitleManager';
import LevelsManager from '@dyl/systems/LevelsManager';
import createSnackbarAction from '../utils/createSnackbarAction';
import wrapperStack from '@dyl/utils/wrapperStack';
import TestsManager from '@dyl/systems/TestsManager';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AudiosManager, MotivationalAudiosManager } from '@dyl/systems/AudiosManager';
import { AudiosAssetsManager, MotivationalAudiosAssetsManager } from '@dyl/systems/AudiosAssetsManager';
import ImagesManager from '@dyl/systems/ImagesManager';
import ImagesAssetsManager from '@dyl/systems/ImagesAssetsManager';
import 'react-awesome-lightbox/build/style.css';
import RubricsManager from '@dyl/systems/RubricManager';
import AmttManager from '@dyl/systems/AmttManager';
import UserManager from '@dyl/systems/UserManager';
import SafeAreaManager from '@dyl/systems/SafeAreaManager';
import SettingsManager from '@dyl/systems/SettingsManager';
import RolesContext from '@dyl/systems/RolesContext';
import PrevPathContext from '@dyl/systems/PrevPathContext';
import { green } from '@mui/material/colors';
import Amplify, { I18n } from 'aws-amplify';
import { Translations } from '@aws-amplify/ui-components';

Amplify.configure(awsExports);

const baseName = process.env.PUBLIC_URL || '/';

const authScreenLabels = {
    en: {
        'Username': 'Email',
        'Enter your username': 'Enter your Email',
        [Translations.INVALID_USERNAME]: 'That user does not exist.' +
        'Please make sure you enter your Email all in lower case.'
    }
};

I18n.setLanguage('en');
I18n.putVocabularies(authScreenLabels);

const useStyles = makeStyles((theme) =>
    ({
        snackBarContainer: {
            zIndex: 9001,
            paddingBottom: '60px',
            height: "auto",
            whiteSpace: "pre-line",
            lineHeight: '28px'
        },
        snackBarSuccess: {
            backgroundColor: green[500]
        },
        snackbarError: {
            backgroundColor: '#d32f2f' // theme.palette.error.main
        }
    })
);

const App = () => {
    const classes = useStyles();

    const snackbarClasses = {
        containerRoot: classes.snackBarContainer,
        variantError: classes.snackbarError,
        variantSuccess: classes.snackBarSuccess
    };

    return wrapperStack()
        .wraps(SafeAreaManager)
        .wraps(UserManager)
        .wraps(SettingsManager)
        .wraps(LevelsManager)
        .wraps(ThemeProvider, { theme })
        .wraps(StyledEngineProvider, { injectFirst: true })
        .wraps(LocalizationProvider, { dateAdapter: AdapterDateFns })
        .wraps(SnackbarProvider, {
            maxSnack: Infinity,
            autoHideDuration: 5000,
            action: createSnackbarAction({ iconComponent: CloseIcon }),
            anchorOrigin: {
                horizontal: 'right',
                vertical: 'bottom'
            },
            classes: snackbarClasses,
        })
        .wraps(AudiosManager)
        .wraps(MotivationalAudiosManager)
        .wraps(AudiosAssetsManager)
        .wraps(MotivationalAudiosAssetsManager)
        .wraps(ImagesManager)
        .wraps(ImagesAssetsManager)
        .wraps(RubricsManager)
        .wraps(TestsManager)
        .wraps(AmttManager)
        .wraps(TitleManager, { default: 'DYL Admin', tabTitleTail: 'DYL' })
        .wraps(BrowserRouter, { basename: baseName })
        .wraps(RolesContext)
        .wraps(PrevPathContext)
        .wraps( OIDCAuthCheck )
        .build();
};

export default App;


